import { Injectable } from '@angular/core';
import { LoginCognitoCallback, CognitoUtil } from '../utils/cognito.service';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { StateManagementService } from './state-management.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { from, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ERROR_CODES } from '../constants/error-codes';
import { checkGenericErrors } from '../errors/generic-error-response';
import { OperationFailedError } from '../errors/errors';
import { LANGUAGE_CONSTANTS as LANG_CONST } from '../constants/language-constants';
import { BRANDS } from '../models/screen-size.enum';
import { CONSTANTS } from '../constants/constants';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class UserLoginService {
  private url = environment.graphqlUrl;

  constructor(
    public cognitoUtil: CognitoUtil,
    private stateManagementService: StateManagementService,
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  private onLoginSuccess = (
    callback: LoginCognitoCallback,
    session: CognitoUserSession
  ) => {
    this.updateNotification().subscribe({
      next: () => {
        if (window.__dev__)
          console.log('Notification preferences updated successfully.');
      },
      error: (err) => {
        if (window.__dev__)
          console.error('Failed to update notification preferences:', err);
      },
    });
    callback.cognitoCallback('', session);
  };

  private onLoginError = (callback: LoginCognitoCallback, err: any) => {
    callback.cognitoCallback(err.message, null);
  };

  authenticate(
    username: string,
    password: string,
    callback: LoginCognitoCallback
  ) {
    let authenticationData = {
      Username: username,
      Password: password,
    };
    let authenticationDetails = new AuthenticationDetails(authenticationData);

    let userData = {
      Username: username,
      Pool: this.cognitoUtil.getUserPool(),
    };
    const brand = this.stateManagementService?.getUserBrand();
    const language = this.stateManagementService?.getUserLanguage();
    const brandName =
      brand === BRANDS.KWIKSETUNITE
        ? CONSTANTS.KWIKSETUNITE
        : CONSTANTS.WEISERUNITE;
    const clientMetadata = {
      brandName: '' + brandName,
      language: '' + language,
    };
    let cognitoUser = new CognitoUser(userData);
    cognitoUser.setAuthenticationFlowType('CUSTOM_AUTH');
    cognitoUser.authenticateUser(authenticationDetails, {
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        callback.cognitoCallback(`User needs to set password.`, null);
      },
      onSuccess: (result) => this.onLoginSuccess(callback, result),
      onFailure: (err) => this.onLoginError(callback, err),
      customChallenge: function (challengeParameters) {
        var current = this;
        challengeParameters.username = cognitoUser?.getUsername();
        callback.handleMFAStep(challengeParameters, function (err, res) {
          if (res.answerType === 'generateCode') {
            var answer =
              'answerType:generateCode,medium:' +
              res.codeType +
              ',codeType:login';
            cognitoUser.sendCustomChallengeAnswer(
              answer,
              current,
              clientMetadata
            );
          } else if (res.answerType === 'regenerateCode') {
            var answer =
              'answerType:regenerateCode,medium:' +
              res.codeType +
              ',codeType:login';
            cognitoUser.sendCustomChallengeAnswer(
              answer,
              current,
              clientMetadata
            );
          } else if (res.answerType === 'verifyCode') {
            var answer =
              'answerType:verifyCode,medium:' +
              res.codeType +
              ',codeType:login,code:' +
              res.code;
            cognitoUser.sendCustomChallengeAnswer(
              answer,
              current,
              clientMetadata
            );
          }
        });
      },
    });
  }

  logout(): Observable<any> {
    return from(
      new Promise((resolve, reject) => {
        try {
          if (this.cognitoUtil.getCurrentUser()) {
            this.cognitoUtil.getCurrentUser()!.signOut();
          }
          this.stateManagementService.logout();
          localStorage.clear();
        } catch (err) {}
        resolve(null);
      })
    );
  }

  getUserAccessibilityMatrix(): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: 'query {  getUserAccessibilityMatrix {  allowedActions  }  }',
      })
      .pipe(
        map((res) => {
          try {
            const resData = JSON.parse(
              res?.data?.getUserAccessibilityMatrix?.allowedActions
            );
            return resData;
          } catch (exception) {
            throw new Error(ERROR_CODES.BAD_ARGUMENT);
          }
        })
      );
  }

  /**
   * validate email already exist or not then share send opt via a email | phone
   * @param email string user define email address
   * @param type type should be phone | email
   * @returns return server response result
   */
  forgotPassword(email: string, type = 'phone'): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation forgotPassword($email: String!, $verificationtype: String!) {  forgotPassword(email: $email, verificationtype: $verificationtype) { ${type} }}`,
        operationName: 'forgotPassword',
        variables: {
          email: `${email}`,
          verificationtype: `${type}`,
        },
      })
      .pipe(
        map((res) => {
          if (res?.data?.forgotPassword) {
            return res?.data;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_SEND_CODE_TRY_AGAIN')
              );
            case ERROR_CODES.BAD_ARGUMENT:
              throw new OperationFailedError(
                this.translate.instant('YOU_MUST_ENTER_A_VALID_EMAIL')
              );

            default:
              throw new Error(
                this.translate.instant('FAILED_TO_SEND_CODE_TRY_AGAIN')
              );
          }
        })
      );
  }

  /**
   *
   * @param code code from email | phone
   * @param email email address
   * @param medium  phone | email
   * @param codetype default forgotpassword server side
   * @returns  return server response result
   */
  passwordRecoveryOTP(
    code: string,
    email: string,
    medium = 'phone',
    codetype = 'forgotpassword'
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation forgotPasswordVerifyCode($code: String!, $codetype: String!, $email: String!, $medium: String!) { forgotPasswordVerifyCode(code: $code, codetype: $codetype, email: $email, medium: $medium) { success } }',
        variables: {
          code: `${code}`,
          codetype: `${codetype}`,
          email: `${email}`,
          medium: `${medium}`,
        },
        operationName: 'forgotPasswordVerifyCode',
      })
      .pipe(
        map((res) => {
          const resData = res?.data;
          if (resData?.forgotPasswordVerifyCode?.success) {
            return resData;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.VALUE_MISS_MATCH:
              throw new OperationFailedError(
                this.translate.instant('VERIFICATION_CODE_IS_INCORRECT')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('OTP_VERIFICATION_FAILED')
              );
              default:
                throw new Error(this.translate.instant('OTP_VERIFICATION_FAILED'));
          }
        })
      );
  }

  /**
   *
   * @param email email address
   * @param password password
   * @returns return server response result
   */
  passwordUpdate(email: string, password: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation forgotPasswordConfirm($email: String!, $password: String!) { forgotPasswordConfirm(email: $email, password: $password) { success } }',
        variables: {
          email: `${email}`,
          password: `${password}`,
        },
        operationName: 'forgotPasswordConfirm',
      })
      .pipe(
        map((res) => {
          const resData = res?.data;
          if (resData?.forgotPasswordConfirm?.success) {
            return resData;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_UPDATED_PASSWORD_TRY_AGAIN')
              );
            default:
              throw new Error(this.translate.instant('FAILED_TO_UPDATED_PASSWORD_TRY_AGAIN'));
          }
        })
      );
  }

  updateNotification(): Observable<any> {
    const brand = this.stateManagementService?.getUserBrand();
    const language = this.stateManagementService?.getUserLanguage();
    const brandName =
      brand === BRANDS.KWIKSETUNITE
        ? CONSTANTS.KWIKSETUNITE
        : CONSTANTS.WEISERUNITE;
    const payload = {
      query: `
        mutation updateNotification($devicetoken: String!, $action: String!, $language: String!, $brandName: String!) {
          updateNotification(devicetoken: $devicetoken, action: $action, language: $language, brandName: $brandName) {
            success
          }
        }
      `,
      variables: {
        devicetoken: 'Web',
        action: 'register',
        language: language as string,
        brandName: brandName as string,
      },
      operationName: 'updateNotification',
    };

    return this.http.post<any>(this.url, payload).pipe(
      map((res) => {
        if (res?.data?.updateNotification?.success) {
          return res?.data;
        } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
          throw new Error(res?.errors[0]?.errorType);
        }
      }),
      catchError((err: any) => {
        throw err;
      })
    );
  }
}
